import React from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from '../TokenAwareTextField';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import {
  SelfAttestItem,
  StepChecklist,
  slugifyKey,
  uniqueKey,
} from '../../../../../support/campaigns';
import { GhostButton } from '../primitives';

/**
 * Author the self-attest checklist for a `content` / `action_item` / `followup` step. Edits the
 * `{ lead, items }` shape persisted at `metadata.checklist` (which `stepChecklist()` reads back). This
 * is the builder analogue of the reference app's authored self-report lists (READINESS_CHECKS,
 * WORKSHOP_READINESS, NEXT_STEPS) and, when the step is flagged `gatesCampaign`, the up-front training
 * gate's enablement items. Pure controlled component: it owns no state, just maps `value`/`onChange`.
 *
 * Each item carries a stable `key` (auto-derived from the label, kept unique within the set) so the
 * SE's ticks on their enrollment survive relabelling — mirrors QuestionSetEditor's key discipline.
 */

const ROLE_OPTIONS: { value: SelfAttestItem['role']; label: string }[] = [
  { value: undefined, label: 'No tag' },
  { value: 'must', label: 'Highly Recommended' },
  { value: 'should', label: 'Recommended' },
  { value: 'nice', label: 'Optional' },
];

export function ChecklistEditor(props: {
  value: StepChecklist;
  onChange: (checklist: StepChecklist) => void;
  disabled?: boolean;
}): JSX.Element {
  const { value, onChange, disabled } = props;
  const lead = value?.lead ?? '';
  const items = value?.items ?? [];

  const setItems = (next: SelfAttestItem[]) => onChange({ lead: value?.lead, items: next });

  const patch = (index: number, next: Partial<SelfAttestItem>) => {
    setItems(items.map((it, i) => (i === index ? { ...it, ...next } : it)));
  };

  const add = () => {
    const key = uniqueKey('item', items.map((it) => it.key));
    setItems([...items, { key, label: '' }]);
  };

  const remove = (index: number) => setItems(items.filter((_, i) => i !== index));

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
  };

  // Keep the key in sync with the label while it is still auto-derivable, but never clobber a
  // tick-bearing key once the label has diverged — only refresh a key still matching its label slug
  // or the placeholder (mirrors QuestionSetEditor.relabel).
  const relabel = (index: number, label: string) => {
    const it = items[index];
    const derivable = !it.label || it.key === slugifyKey(it.label) || /^item(-\d+)?$/.test(it.key);
    const next: Partial<SelfAttestItem> = { label };
    if (derivable) {
      next.key = uniqueKey(label || 'item', items.filter((_, i) => i !== index).map((x) => x.key));
    }
    patch(index, next);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        A checklist the SE ticks off in the runner. Ticks are saved on their enrollment; ticking every
        item marks the step done.
      </Typography>

      <TextField
        value={lead}
        onChange={(e) => onChange({ lead: e.target.value || undefined, items })}
        label="Intro line (optional)"
        size="small"
        fullWidth
        disabled={disabled}
      />

      {items.length === 0 && (
        <Typography variant="body2" color="text.disabled">
          No items yet.
        </Typography>
      )}

      {items.map((it, index) => (
        <Box
          key={index}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 18 }}>
                {index + 1}.
              </Typography>
              <TextField
                value={it.label}
                onChange={(e) => relabel(index, e.target.value)}
                label="Item"
                size="small"
                fullWidth
                disabled={disabled}
              />
              <Tooltip title="Move up">
                <span>
                  <IconButton size="small" onClick={() => move(index, -1)} disabled={disabled || index === 0}>
                    <ArrowUpwardOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => move(index, 1)}
                    disabled={disabled || index === items.length - 1}
                  >
                    <ArrowDownwardOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove">
                <span>
                  <IconButton size="small" onClick={() => remove(index)} disabled={disabled}>
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                value={it.hint ?? ''}
                onChange={(e) => patch(index, { hint: e.target.value || undefined })}
                label="Hint (optional)"
                size="small"
                fullWidth
                disabled={disabled}
              />
              <TextField
                select
                value={it.role ?? ''}
                onChange={(e) =>
                  patch(index, { role: (e.target.value || undefined) as SelfAttestItem['role'] })
                }
                label="Tag"
                size="small"
                sx={{ minWidth: 140 }}
                disabled={disabled}
              >
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r.label} value={r.value ?? ''}>
                    {r.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
        </Box>
      ))}

      <Box>
        <GhostButton onClick={add} disabled={disabled}>
          <AddOutlinedIcon fontSize="small" style={{ marginRight: 4 }} />
          Add item
        </GhostButton>
      </Box>
    </Stack>
  );
}

export default ChecklistEditor;
